import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/.dev/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/.dev/markdoc/tags.tsx'
import * as nodes from '/vercel/path0/.dev/markdoc/nodes.tsx'
import * as functions from '/vercel/path0/.dev/markdoc/functions.ts'
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Column values\ndescription: Column values are the values in the tables of your data source.\nvideo:\nslug: column-values\n---\n\nWhen you work with any component or list layout, you're usually displaying values from a column in your table. These are therefore referred to as Column Values.\n\nFor example, in this app, the list layout is displaying the Column Value 'Item' for each item's title. It is also displaying the Column Value 'Collection' as the header.\n\n![](/docs/reference/values/column-values/Screen%20Shot%202021-01-14%20at%209.11.23%20AM.png)\n\nIn the app below, the title component is displaying a mixture of Column Values and Custom Values.\n\n- The title is displaying the 'Item' column value\n- The details is displaying the custom value of \"Item Details\"\n- The image is displaying the column value 'picture'.\n\n{% callout title=\"\" %}\nIf we wanted to change the details to be a column value again, we could click the three-dot menu and select the column.\n{% /callout %}\n\n![](/docs/reference/values/column-values/Screen%20Shot%202021-01-14%20at%209.12.18%20AM.png)\n\n## Column values in submission screens\n\nBut column values are also used in submission screens – in other words; **Add, Edit** & **Form** screens. They can be used to send values from the current item.\n\n{% video src=\"https://youtu.be/2XEf6BVYciA\" /%}\n\nFor example, in the app below the **`Title`** and **`Price`** of the item get passed into the sheet with the form submission without the user having to do anything.\n\n![](/docs/reference/values/column-values/2019-11-21%2010.37.48.gif)\n\nWhen you submit a **form** you ask users to fill in data and this gets sent to your sheet. Sometimes those forms are within list items that contain data that's relevant to the form that's being submitted and this is where column values are useful.\n\nFor example, the app below is an app for a store. Users can browse items and buy them with the **`Buy Button`**. But they can also submit a form to ask questions about the item they are looking at.\n\n{% callout title=\"\" %}\nThe Buy Button is no longer available in Glide Apps. Instead, you can use [Stripe Integration](/docs/stripe) to set up a payment gateway. However, if you're using classic apps, you can continue to use the Buy Button.\n{% /callout %}\n\nWhen they tap 'Ask a question' a form is triggered and the submissions go to our submissions sheet.\n\n![](/docs/reference/values/column-values/1.gif)\n\nIn the example above, this form is obviously not that helpful as the owner of the app won't know which item the user is referring to. The owner of the app could create multiple **`Text fields`** that ask the user which item they are referring to but that is not a great user experience.\n\nInstead, we can pass column values from the item itself into the form so that when a form is submitted, the name, price and any other details about that item gets submitted with the form.\n\n![](/docs/reference/values/column-values/2.gif)\n\nWhen you customize the components within a form, you will be able to choose normal **`components`**, **`special values`** AND 'Column Values'. The column values will be every column in that sheet and therefore will always be different, depending on the columns in that sheet.\n\nHere we can see that our column values are `Product`, `Description`, `Price` and `Picture` 1, because those are the columns in the sheet that our **`Form button`** is located in.\n\n![](/docs/reference/values/column-values/Group%207.png)\n\nSimply add these columns to your form when you are customizing it and make sure that they are matched with an empty column in your submissions sheet. Then, when your form is submitted, these column values will be passed along with the submission so it's clear which item the form was submitted in.\n\n![](/docs/reference/values/column-values/3.gif)\n\nAdding column values and **special values** to a form can make the experience very simple for your users and also pass a lot of useful information to your sheet.\n";
const filepath = "/reference/values/column-values.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
